import api from './api';
import type { PlayerProfile } from './playerProfileService';

// === Embedded Sub-Types ===

export interface ProTeamPlayer {
  playerId?: string | null;
  name?: string | null;
  nickname?: string | null;
  role?: string | null;
  seed?: number | null;
  countryCode?: string | null;
  flag?: string | null;
  profileImage?: string | null;
  worldRanking?: number | null;
  // wenn ?expand=players: vollständiges PlayerProfile
  profile?: PlayerProfile | null;
}

export interface ProTeam {
  id?: string;
  name?: string;
  shortName?: string | null;
  countryCode?: string | null;
  flag?: string | null;
  seed?: number | null;
  group?: string | null;
  isSeeded?: boolean;
  eliminatedInRound?: string | null;
  finalPosition?: number | null;
  players?: ProTeamPlayer[];
  teamImage?: string | null;
  bio?: string | null;
  titlesWithTeam?: number | null;
}

export interface ProMatchSide {
  teamId?: string | null;
  teamName?: string | null;
  score?: number | null;
  average?: number | null;
  highestCheckout?: number | null;
  checkouts100Plus?: number | null;
  nineDarters?: number | null;
  isWinner?: boolean;
}

export interface ProMatch {
  id?: string;
  round?: string;
  roundLabel?: string | null;
  group?: string | null;
  matchNumber?: number | null;
  scheduledAt?: number | null;
  scheduledSession?: string | null;
  venue?: string | null;
  status?: 'SCHEDULED' | 'LIVE' | 'FINISHED' | 'POSTPONED' | 'CANCELLED' | null;
  format?: string | null;
  home?: ProMatchSide;
  away?: ProMatchSide;
  winnerTeamId?: string | null;
  streamUrl?: string | null;
  notes?: string | null;
}

export interface ProPrizeBreakdown {
  position?: string;
  amount?: number | null;
  currency?: string | null;
  perTeam?: boolean;
  count?: number | null;
  bonusType?: string | null;
  note?: string | null;
}

export interface ProPastWinner {
  year?: number;
  winnerTeamName?: string | null;
  winnerPlayerIds?: string[];
  runnerUpTeamName?: string | null;
  score?: string | null;
  venue?: string | null;
  prizeMoney?: number | null;
  currency?: string | null;
  note?: string | null;
}

export interface ProKeyFact {
  icon?: string | null;
  label?: string;
  value?: string;
  sublabel?: string | null;
  order?: number;
}

export interface ProScheduleEntry {
  date?: number | null;
  dateLabel?: string | null;
  sessionLabel?: string | null;
  round?: string | null;
  description?: string | null;
  matchIds?: string[];
}

export interface ProRecord {
  label?: string;
  value?: string;
  holder?: string | null;
  holderPlayerId?: string | null;
  year?: number | null;
  note?: string | null;
}

export interface ProFormatRule {
  label?: string;
  value?: string;
  order?: number;
}

export interface ProFaqEntry {
  question?: string | null;
  answer?: string | null;
  order?: number;
  /** SEO-Alias-Paket (2026-09-20): Anker-Id der Prerender-Sektion (z. B. 'preisgeld') */
  anchor?: string | null;
}

/**
 * SEO-Alias-Paket des Backends (SEO Block A, 2026-09-20). Server-Prerender und
 * Web zeigen dieselben Title/H1/FAQs; `aliases` sind alle Namensvarianten
 * (manuell + generiert), `faqs` die alias-bewussten FAQs mit Sektions-Anker.
 */
export interface ProTournamentSeo {
  title: string;
  description: string;
  h1: string;
  akaLine: string;
  introSentence: string;
  aliases: string[];
  faqs: ProFaqEntry[];
}

export interface ProNotableNineDarter {
  year?: number | null;
  playerId?: string | null;
  playerName?: string | null;
  round?: string | null;
  opponent?: string | null;
  note?: string | null;
}

export interface ProQualificationCriterion {
  label?: string;
  value?: string;
  order?: number;
  count?: number | null;
}

export interface ProParallelCompetition {
  name?: string;
  description?: string | null;
  prizeMoney?: number | null;
  prizeMoneyLabel?: string | null;
  winnerName?: string | null;
  participants?: string[];
}

export interface ProSponsorHistoryEntry {
  year?: number;
  sponsor?: string | null;
  prizeMoney?: number | null;
}

export interface ProDrawPairing {
  round?: string | null;
  homeTeamName?: string | null;
  awayTeamName?: string | null;
  homePlayerId?: string | null;
  awayPlayerId?: string | null;
  order?: number;
  /** Spielplan-Erweiterung (2026-08-28, alle nullable — Alt-Daten ohne) */
  dayLabel?: string | null; // "Freitag, 28. August 2026, 13 bis 17 Uhr"
  roundLabel?: string | null; // "1. Runde, Best of 11 Legs"
  timeLabel?: string | null; // "15:00"
  scheduledAt?: number | null; // epoch ms
  score?: string | null; // "6:4" (nur quellenbelegt)
  winner?: 'home' | 'away' | string | null;
}

export interface ProBroadcaster {
  name?: string | null;
  countryCode?: string | null;
  url?: string | null;
  logo?: string | null;
}

export interface ProGroup {
  name?: string | null;
  teamIds?: string[];
}

// === Main Interface ===

export interface ProTournamentInfo {
  _id?: string;
  id?: string;
  slug?: string;
  createdAt?: number;
  updatedAt?: number | null;

  /**
   * FlashLive-Stage-Id der Edition (z. B. 'UH4KYkR1' = ADM 2026).
   * Im Admin pflegbar; ist sie gesetzt, zeigt die Turnierseite einen
   * Ergebnis-Bereich. Nullable — alte Backends liefern das Feld gar nicht.
   */
  flashliveStageId?: string | null;

  name?: string;
  shortName?: string | null;
  edition?: string | null;
  year?: number | null;
  organization?: string;
  tier?: string | null;
  tournamentType?: 'TEAM' | 'SINGLES' | 'PAIRS' | 'MIXED' | string | null;
  discipline?: string | null;

  status?: 'UPCOMING' | 'LIVE' | 'COMPLETED' | 'CANCELLED' | null;
  visible?: boolean;
  featured?: boolean;

  startDate?: number | null;
  endDate?: number | null;
  dateLabel?: string | null;

  venue?: string | null;
  city?: string | null;
  country?: string | null;
  countryCode?: string | null;
  capacity?: number | null;

  totalPrizeMoney?: number | null;
  prizeMoneyCurrency?: string | null;
  prizeMoneyLabel?: string | null;
  prizeBreakdown?: ProPrizeBreakdown[];

  formatSummary?: string | null;
  formatRules?: ProFormatRule[];

  keyFacts?: ProKeyFact[];

  titleHolderTeamName?: string | null;
  titleHolderPlayerIds?: string[];

  records?: ProRecord[];

  teams?: ProTeam[];
  totalTeams?: number | null;

  hasGroupStage?: boolean;
  groups?: ProGroup[];

  matches?: ProMatch[];

  schedule?: ProScheduleEntry[];

  pastWinners?: ProPastWinner[];
  firstHeldYear?: number | null;

  faqs?: ProFaqEntry[];
  trophyName?: string | null;
  trophyImage?: string | null;
  notableNineDarters?: ProNotableNineDarter[];
  qualificationCriteria?: ProQualificationCriterion[];
  parallelCompetitions?: ProParallelCompetition[];
  sponsorHistory?: ProSponsorHistoryEntry[];
  drawPairings?: ProDrawPairing[];

  logo?: string | null;
  heroImage?: string | null;
  thumbnail?: string | null;
  sponsor?: string | null;
  sponsorLogo?: string | null;

  overview?: string | null;
  intro?: string | null;

  broadcasters?: ProBroadcaster[];
  officialWebsite?: string | null;

  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string[];
  /** Manuelle Aliase (Admin); erster Eintrag = Hauptname/H1 */
  seoAliases?: string[];
  /** Vom Backend gerechnetes Alias-Paket (nullable, alte Backends liefern nichts) */
  seo?: ProTournamentSeo | null;

  tags?: string[];
  views?: number;
  relatedTournamentSlugs?: string[];

  // Aus expand=players resolved
  _expandedPlayers?: boolean;
}

export interface ProTournamentListResponse {
  tournaments?: ProTournamentInfo[];
}

/**
 * Client-Fallback: Effektiven Status aus der Timeline ableiten (Spiegel der
 * Backend-Logik deriveEffectiveStatus). Nötig, weil gecachte/alte Backend-
 * Antworten den Status nicht nachziehen — z.B. stand der World Cup of Darts
 * nach Turnier-Ende noch wochenlang auf UPCOMING.
 */
/**
 * Turnier-Start/Ende sind KALENDERTAGE, gespeichert als UTC-Tagesgrenzen
 * (startDate 00:00:00Z, endDate 23:59:59Z – 28 von 34 Turnieren, 2026-09-17).
 * Lokale Datumsteile (getDate) kippen das Ende in MESZ auf den Folgetag
 * („17.–21. Sep." statt „17.–20. Sep." bei den World Series Finals).
 * Liefert ein lokales Date mit den UTC-Datumsteilen – für alle Formatierer.
 */
export const tournamentDay = (ms: number): Date => {
  const d = new Date(ms);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

export const deriveEffectiveStatus = (
  t: Pick<ProTournamentInfo, 'status' | 'startDate' | 'endDate' | 'schedule'> | null | undefined,
  now: number = Date.now()
): ProTournamentInfo['status'] => {
  const status = t?.status ?? undefined;
  if (!t || status === 'CANCELLED') return status;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const endDate = typeof t.endDate === 'number' ? t.endDate : null;
  const startDate = typeof t.startDate === 'number' ? t.startDate : null;
  if (endDate !== null && endDate + DAY_MS < now) return 'COMPLETED';
  if (
    (status === 'UPCOMING' || status === 'LIVE') &&
    startDate !== null &&
    startDate <= now &&
    (endDate === null || now <= endDate + DAY_MS)
  ) {
    // SCHEDULE-REGEL (2026-08-27): Saison-Dachturniere (PDC Pro Tour Feb–Nov,
    // Premier League) sind nur LIVE, wenn HEUTE eine Session im (KI-gepflegten)
    // Schedule steht — sonst bleiben sie UPCOMING. Ohne datierten Schedule:
    // Spannen-Logik wie bisher (Einzel-Events ohne Detailplan).
    const dated = (t.schedule ?? []).filter((e) => typeof e.date === 'number');
    if (dated.length > 0) {
      const day = new Date(now);
      const sessionToday = dated.some((e) => {
        const d = new Date(e.date as number);
        return (
          d.getFullYear() === day.getFullYear() &&
          d.getMonth() === day.getMonth() &&
          d.getDate() === day.getDate()
        );
      });
      if (!sessionToday) return 'UPCOMING';
    }
    return 'LIVE';
  }
  return status;
};

const withEffectiveStatus = (t: ProTournamentInfo): ProTournamentInfo => ({
  ...t,
  status: deriveEffectiveStatus(t),
});

class ProTournamentInfoService {
  async list(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    organization?: string;
    year?: number;
    search?: string;
  }): Promise<ProTournamentInfo[]> {
    try {
      const res = await api.get<ProTournamentListResponse>('/api/pro-tournaments', {
        params: {
          limit: params?.limit ?? 50,
          offset: params?.offset ?? 0,
          status: params?.status,
          organization: params?.organization,
          year: params?.year,
          search: params?.search,
        },
      });
      return (res.data?.tournaments ?? []).map(withEffectiveStatus);
    } catch (err) {
      console.error('Fehler beim Laden der ProTournamentInfo-Liste:', err);
      return [];
    }
  }

  async getById(id: string, options?: { expand?: boolean }): Promise<ProTournamentInfo | null> {
    if (!id) return null;
    try {
      const res = await api.get<ProTournamentInfo>(`/api/pro-tournament/id/${id}`, {
        params: { expand: options?.expand ? 'players' : undefined },
      });
      return res.data ? withEffectiveStatus(res.data) : null;
    } catch (err) {
      console.error('Fehler beim Laden der ProTournamentInfo by ID:', err);
      return null;
    }
  }

  async getBySlug(slug: string, options?: { expand?: boolean }): Promise<ProTournamentInfo | null> {
    if (!slug) return null;
    try {
      const res = await api.get<ProTournamentInfo>(`/api/pro-tournament/slug/${slug}`, {
        params: { expand: options?.expand ? 'players' : undefined },
      });
      return res.data ? withEffectiveStatus(res.data) : null;
    } catch (err) {
      console.error('Fehler beim Laden der ProTournamentInfo by Slug:', err);
      return null;
    }
  }

  /**
   * Alias-Slug (z. B. 'darts-wm-2027') auf den kanonischen Slug auflösen.
   * 404/Fehler → null, damit die Seite wie bisher „nicht gefunden" zeigen kann.
   */
  async resolveSlug(slug: string): Promise<string | null> {
    if (!slug) return null;
    try {
      const res = await api.get<{ slug?: string; path?: string }>(
        `/api/pro-tournament/resolve/${encodeURIComponent(slug)}`
      );
      const resolved = res.data?.slug;
      return typeof resolved === 'string' && resolved.trim() ? resolved.trim() : null;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) console.error('Fehler beim Auflösen des Turnier-Alias:', err);
      return null;
    }
  }

  // === Helpers ===
  displayName(t?: ProTournamentInfo | null): string {
    if (!t) return '';
    const base = t.name || t.shortName || '';
    if (t.edition) return `${base} ${t.edition}`.trim();
    return base;
  }

  formatPrize(t?: ProTournamentInfo | null): string {
    if (!t) return '';
    if (t.prizeMoneyLabel) return t.prizeMoneyLabel;
    if (typeof t.totalPrizeMoney === 'number') {
      const currency = t.prizeMoneyCurrency || '';
      const formatted = t.totalPrizeMoney.toLocaleString('de-DE');
      return currency ? `${currency} ${formatted}` : formatted;
    }
    return '';
  }

  formatAmount(amount?: number | null, currency?: string | null): string {
    if (amount === null || amount === undefined) return '';
    const formatted = amount.toLocaleString('de-DE');
    if (!currency) return formatted;
    if (currency === 'GBP') return `£${formatted}`;
    if (currency === 'EUR') return `€${formatted}`;
    if (currency === 'USD') return `$${formatted}`;
    return `${currency} ${formatted}`;
  }

  getSeededTeams(t?: ProTournamentInfo | null): ProTeam[] {
    if (!t?.teams) return [];
    return t.teams
      .filter((team) => team.isSeeded || (typeof team.seed === 'number' && team.seed > 0))
      .sort((a, b) => (a.seed ?? 999) - (b.seed ?? 999));
  }

  getNonSeededTeams(t?: ProTournamentInfo | null): ProTeam[] {
    if (!t?.teams) return [];
    return t.teams.filter((team) => !team.isSeeded && !team.seed);
  }

  getKeyFactsSorted(t?: ProTournamentInfo | null): ProKeyFact[] {
    if (!t?.keyFacts) return [];
    return [...t.keyFacts].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  getFormatRulesSorted(t?: ProTournamentInfo | null): ProFormatRule[] {
    if (!t?.formatRules) return [];
    return [...t.formatRules].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  getScheduleSorted(t?: ProTournamentInfo | null): ProScheduleEntry[] {
    if (!t?.schedule) return [];
    return [...t.schedule];
  }

  getPastWinnersSorted(t?: ProTournamentInfo | null): ProPastWinner[] {
    if (!t?.pastWinners) return [];
    return [...t.pastWinners].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
  }

  hasSection(t: ProTournamentInfo | null | undefined, section:
    | 'teams' | 'matches' | 'pastWinners' | 'records' | 'schedule'
    | 'prize' | 'format' | 'groups' | 'broadcasters'
    | 'nineDarters' | 'qualification' | 'parallel' | 'sponsorHistory' | 'draw'
  ): boolean {
    if (!t) return false;
    switch (section) {
      case 'teams': return Array.isArray(t.teams) && t.teams.length > 0;
      case 'matches': return Array.isArray(t.matches) && t.matches.length > 0;
      case 'pastWinners': return Array.isArray(t.pastWinners) && t.pastWinners.length > 0;
      case 'records': return Array.isArray(t.records) && t.records.length > 0;
      case 'schedule': return Array.isArray(t.schedule) && t.schedule.length > 0;
      case 'prize': return Array.isArray(t.prizeBreakdown) && t.prizeBreakdown.length > 0;
      case 'format': return Array.isArray(t.formatRules) && t.formatRules.length > 0;
      case 'groups': return !!t.hasGroupStage && Array.isArray(t.groups) && t.groups.length > 0;
      case 'broadcasters': return Array.isArray(t.broadcasters) && t.broadcasters.length > 0;
      case 'nineDarters': return Array.isArray(t.notableNineDarters) && t.notableNineDarters.length > 0;
      case 'qualification': return Array.isArray(t.qualificationCriteria) && t.qualificationCriteria.length > 0;
      case 'parallel': return Array.isArray(t.parallelCompetitions) && t.parallelCompetitions.length > 0;
      case 'sponsorHistory': return Array.isArray(t.sponsorHistory) && t.sponsorHistory.length > 0;
      case 'draw': return Array.isArray(t.drawPairings) && t.drawPairings.length > 0;
      default: return false;
    }
  }
}

export interface DrawRoundGroup {
  roundLabel: string | null;
  pairs: ProDrawPairing[];
}

export interface DrawDayGroup {
  dayLabel: string | null;
  rounds: DrawRoundGroup[];
}

const GERMAN_MONTHS: Record<string, number> = {
  januar: 0, februar: 1, märz: 2, maerz: 2, april: 3, mai: 4, juni: 5,
  juli: 6, august: 7, september: 8, oktober: 9, november: 10, dezember: 11,
};

/** Datum (ohne Session-Stunde) aus einem deutschen Tages-Label (PURE). */
export function parseGermanDayDateOnly(label?: string | null): number | null {
  if (!label) return null;
  const m = label.toLowerCase().match(/(\d{1,2})\.\s*([a-zä]+)\s*(\d{4})/);
  if (!m) return null;
  const month = GERMAN_MONTHS[m[2]];
  if (month == null) return null;
  return Date.UTC(Number(m[3]), month, Number(m[1]));
}

/**
 * Parst ein deutsches Tages-Label ("Freitag, 28. August 2026, 13 bis 17 Uhr")
 * zu epoch ms (PURE) — inkl. Session-Startstunde, damit Nachmittags- vor
 * Abendsession sortiert. null, wenn nicht parsebar.
 */
export function parseGermanDayLabel(label?: string | null): number | null {
  const base = parseGermanDayDateOnly(label);
  if (base == null) return null;
  const hour = (label as string).match(/(\d{1,2})(?::\d{2})?\s*(?:bis|–|-|Uhr)/);
  return base + (hour ? Number(hour[1]) : 0) * 3600000;
}

/** "19:30" → Minuten seit Mitternacht (PURE), sonst null. */
export function parseTimeLabelMinutes(label?: string | null): number | null {
  const m = (label ?? '').match(/^(\d{1,2}):(\d{2})$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/**
 * Gruppiert die Auslosungs-Paarungen für die dartn-artige Spielplan-Ansicht
 * (PURE): GLOBAL nach dayLabel gruppiert (nicht konsekutiv — der KI-Sync
 * vergibt order teils nach Bracket statt nach Session, wodurch sich Tage
 * sonst fragmentiert wiederholten, Befund 2026-08-28). Tage chronologisch
 * über scheduledAt bzw. geparstes dayLabel; innerhalb eines Tages Runden
 * nach erster zeitlicher Nennung, Paarungen nach Uhrzeit, dann order.
 */
export function groupDrawPairings(pairings: ProDrawPairing[]): DrawDayGroup[] {
  const pairTime = (p: ProDrawPairing): number | null => {
    if (typeof p.scheduledAt === 'number') return p.scheduledAt;
    // Mit konkreter Uhrzeit zählt NUR Datum + Uhrzeit — sonst würde die
    // Session-Startstunde aus dem Label doppelt addiert (Fix 2026-08-28).
    const minutes = parseTimeLabelMinutes(p.timeLabel);
    if (minutes != null) {
      const date = parseGermanDayDateOnly(p.dayLabel);
      return date == null ? null : date + minutes * 60000;
    }
    return parseGermanDayLabel(p.dayLabel);
  };

  type DayBucket = {
    dayLabel: string | null;
    firstIdx: number;
    minTime: number;
    rounds: Map<string, { roundLabel: string | null; firstIdx: number; minTime: number; pairs: { pair: ProDrawPairing; idx: number; time: number | null }[] }>;
  };

  const days = new Map<string, DayBucket>();
  pairings.forEach((pair, idx) => {
    const dayLabel = pair.dayLabel ?? null;
    const dayKey = dayLabel ?? '__none__';
    const time = pairTime(pair);
    let day = days.get(dayKey);
    if (!day) {
      day = { dayLabel, firstIdx: idx, minTime: Infinity, rounds: new Map() };
      days.set(dayKey, day);
    }
    if (time != null && time < day.minTime) day.minTime = time;

    const roundLabel = pair.roundLabel ?? pair.round ?? null;
    const roundKey = roundLabel ?? '__none__';
    let round = day.rounds.get(roundKey);
    if (!round) {
      round = { roundLabel, firstIdx: idx, minTime: Infinity, pairs: [] };
      day.rounds.set(roundKey, round);
    }
    if (time != null && time < round.minTime) round.minTime = time;
    round.pairs.push({ pair, idx, time });
  });

  const byTimeThenIdx = (aTime: number, aIdx: number, bTime: number, bIdx: number) =>
    aTime !== bTime ? aTime - bTime : aIdx - bIdx;

  return Array.from(days.values())
    .sort((a, b) => byTimeThenIdx(a.minTime, a.firstIdx, b.minTime, b.firstIdx))
    .map((day) => ({
      dayLabel: day.dayLabel,
      rounds: Array.from(day.rounds.values())
        .sort((a, b) => byTimeThenIdx(a.minTime, a.firstIdx, b.minTime, b.firstIdx))
        .map((round) => ({
          roundLabel: round.roundLabel,
          pairs: round.pairs
            .sort((a, b) => byTimeThenIdx(
              a.time ?? Infinity, a.pair.order ?? a.idx,
              b.time ?? Infinity, b.pair.order ?? b.idx,
            ))
            .map((entry) => entry.pair),
        })),
    }));
}

/** true, wenn die Paarungen Spielplan-Infos tragen (neues KI-Sync-Format). */
export function drawPairingsHaveScheduleInfo(pairings: ProDrawPairing[]): boolean {
  return pairings.some(
    (p) => !!p.dayLabel || !!p.roundLabel || !!p.timeLabel || !!p.score
  );
}

export default new ProTournamentInfoService();
